import axios, {
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import decamelizeKeys from 'decamelize-keys';
import camelcaseKeys from 'camelcase-keys';

import { NPT_API_URL } from '@config';
import i18n from '@i18n/i18n';

import { useAuthStore } from '@features/auth';
import { isValidToken } from '@utils';

// #############################################################################
// # interceptors
// #############################################################################

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config };
  const accessToken = useAuthStore.getState().accessToken;

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (newConfig.headers['Content-Type'] === 'multipart/form-data')
    return newConfig;

  if (config.params) {
    newConfig.params = decamelizeKeys(config.params, { deep: true });
  }
  if (config.data) {
    newConfig.data = decamelizeKeys(config.data, { deep: true });
  }

  newConfig.headers['Accept-Language'] = i18n.language;
  return newConfig;
};

export async function refreshToken() {
  const refreshToken = useAuthStore.getState().refreshToken;

  // If the refreshToken is not valid, logout the user by deleting the tokens.
  if (!refreshToken || !isValidToken(refreshToken)) {
    useAuthStore.getState().deleteTokens();
    return;
  }
  const refreshBody = { refresh: JSON.parse(refreshToken) };

  try {
    const response = await axiosRequest.post(
      `/auth/token/refresh/`,
      refreshBody
    );
    useAuthStore
      .getState()
      .setTokens({
        accessToken: response.data.access,
        refreshToken: response.data.refresh,
      });
    return response.data.access;
  } catch (error) {
    console.log('DELETE TOKENS - axios refreshToken', error);
    useAuthStore.getState().deleteTokens();
  }
}

// #############################################################################
// # axiosRequest
// #############################################################################

export const axiosRequest = axios.create({
  baseURL: NPT_API_URL,
  timeout: 0,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

axiosRequest.interceptors.request.use((config) => requestInterceptor(config));

axiosRequest.interceptors.response.use((response: AxiosResponse) => {
  if (
    response.data &&
    response.headers['content-type'] === 'application/json'
  ) {
    response.data = camelcaseKeys(response.data, { deep: true });
  }
  return response;
});

// #############################################################################
// # axiosAuthenticatedRequest
// #############################################################################

export const axiosAuthenticatedRequest = axios.create({
  baseURL: NPT_API_URL,
  timeout: 0,
  headers: {
    // Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
    'Content-Type': 'application/json',
    // 'Accept-Language': i18n.language,
  },
});

axiosAuthenticatedRequest.interceptors.request.use((config) =>
  requestInterceptor(config)
);

async function errorInterceptor(error: AxiosError) {
  const originalRequest = error.config;

  if (error.response?.status === 401) {
    try {
      const newAccessToken = await refreshToken();
      if (!newAccessToken) return;

      const headerAuthorization = `Bearer ${newAccessToken}`;
      axiosAuthenticatedRequest.defaults.headers['Authorization'] =
        headerAuthorization;
      if (originalRequest && originalRequest.headers) {
        originalRequest.headers['Authorization'] = headerAuthorization;
      }
      if (originalRequest) {
        const response = await axiosAuthenticatedRequest(originalRequest);
        if (response.status !== 200) {
          console.log('DELETE TOKENS - axios errorInterceptor');
          useAuthStore.getState().deleteTokens();
          axiosAuthenticatedRequest.defaults.headers['Authorization'] = '';
          window.location.reload();
        }
        return response;
      }
    } catch (error) {
      // if token refresh fails, logout the user to avoid potential security risks.
      console.log('DELETE TOKENS - axios errorInterceptor error', error);
      useAuthStore.getState().deleteTokens();
      axiosAuthenticatedRequest.defaults.headers['Authorization'] = '';
      window.location.reload();
    }
  }

  // Ensure that the error response data is camelCased
  if (
    error.response?.data &&
    error.response.headers['content-type'] === 'application/json'
  ) {
    error.response.data = camelcaseKeys(
      error.response.data as Record<string, unknown>,
      { deep: true }
    );
  }

  return Promise.reject(error);
}

axiosAuthenticatedRequest.interceptors.response.use(
  (response: AxiosResponse) => {
    if (
      response.data &&
      response.headers['content-type'] === 'application/json'
    ) {
      response.data = camelcaseKeys(response.data, { deep: true });
    }
    return response;
  },
  errorInterceptor
);

export const setlanguageHeader = (language: string) => {
  axiosRequest.defaults.headers['Accept-Language'] = language;
  axiosAuthenticatedRequest.defaults.headers['Accept-Language'] = language;
};
